import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Square, Clock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const getDisplayName = (user) => {
  if (user?.first_name || user?.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }
  return user?.full_name || user?.email || "";
};

export default function TimeTracker({ projectId, projectName, taskId, taskTitle, relatedToType, relatedToId, relatedToName }) {
  const [description, setDescription] = useState("");
  const [billable, setBillable] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => base44.auth.me(),
  });

  const { data: runningEntry } = useQuery({
    queryKey: ["runningTimeEntry", user?.email],
    queryFn: () => base44.entities.TimeEntry.filter({ 
      user_email: user?.email, 
      is_running: true 
    }),
    enabled: !!user?.email,
    refetchInterval: 1000,
  });

  const currentRunning = runningEntry?.[0];

  useEffect(() => {
    if (currentRunning?.start_time) {
      const interval = setInterval(() => {
        const start = new Date(currentRunning.start_time);
        const now = new Date();
        const diff = Math.floor((now - start) / 1000);
        setElapsedTime(diff);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setElapsedTime(0);
    }
  }, [currentRunning]);

  const startTimerMutation = useMutation({
    mutationFn: (data) => base44.entities.TimeEntry.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["runningTimeEntry"] });
      queryClient.invalidateQueries({ queryKey: ["timeEntries"] });
      queryClient.invalidateQueries({ queryKey: ["project-time-entries"] });
    },
  });

  const stopTimerMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.TimeEntry.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["runningTimeEntry"] });
      queryClient.invalidateQueries({ queryKey: ["timeEntries"] });
      queryClient.invalidateQueries({ queryKey: ["project-time-entries"] });
      setDescription("");
    },
  });

  const handleStart = () => {
    startTimerMutation.mutate({
      user_email: user?.email,
      user_name: getDisplayName(user),
      project_id: projectId || null,
      project_name: projectName || null,
      task_id: taskId || null,
      task_title: taskTitle || null,
      related_to_type: relatedToType || "none",
      related_to_id: relatedToId || null,
      related_to_name: relatedToName || null,
      start_time: new Date().toISOString(),
      description: description || "",
      is_running: true,
      billable: billable,
    });
  };

  const handleStop = () => {
    if (!currentRunning) return;
    const endTime = new Date();
    const startTime = new Date(currentRunning.start_time);
    const durationMinutes = Math.floor((endTime - startTime) / 60000);

    stopTimerMutation.mutate({
      id: currentRunning.id,
      data: {
        ...currentRunning,
        end_time: endTime.toISOString(),
        duration_minutes: durationMinutes,
        is_running: false,
      },
    });
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const isRunningForThisContext = currentRunning && 
    (!projectId || currentRunning.project_id === projectId) &&
    (!taskId || currentRunning.task_id === taskId);

  return (
    <Card className="p-4 bg-white border-0 shadow-sm">
      <div className="flex items-center gap-3 mb-3">
        <Clock className="w-5 h-5 text-zinc-900" />
        <h3 className="font-semibold text-slate-900">Tidsregistrering</h3>
      </div>

      {currentRunning ? (
        <div className="space-y-3">
          <div className="p-4 bg-lime-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-zinc-900 font-medium">Timer kjører</span>
              <span className="text-2xl font-bold text-zinc-900 tabular-nums">
                {formatTime(elapsedTime)}
              </span>
            </div>
            {currentRunning.project_name && (
              <p className="text-xs text-zinc-900">📁 {currentRunning.project_name}</p>
            )}
            {currentRunning.task_title && (
              <p className="text-xs text-zinc-900">✓ {currentRunning.task_title}</p>
            )}
            {currentRunning.description && (
              <p className="text-xs text-zinc-900 mt-1">{currentRunning.description}</p>
            )}
          </div>
          <Button 
            onClick={handleStop} 
            className="w-full bg-rose-600 hover:bg-rose-700"
            disabled={stopTimerMutation.isPending}
          >
            <Square className="w-4 h-4 mr-2" />
            Stopp Timer
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <Textarea
            placeholder="Beskrivelse av arbeidet..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
          <div className="flex items-center gap-2">
            <Checkbox 
              id="billable"
              checked={billable}
              onCheckedChange={(checked) => setBillable(checked)}
            />
            <Label htmlFor="billable" className="cursor-pointer text-sm">
              Fakturerbar tid
            </Label>
          </div>
          <Button 
            onClick={handleStart} 
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            disabled={startTimerMutation.isPending}
          >
            <Play className="w-4 h-4 mr-2" />
            Start Timer
          </Button>
        </div>
      )}
    </Card>
  );
}